import { useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import {
  X, Mic, CheckCircle2, Upload, Loader2, ShieldCheck, ChevronLeft, ChevronRight,
  User, Briefcase, Sparkles, FileText, Image as ImageIcon, Trash2, Info,
} from "lucide-react";
import api from "@/lib/api";
import { useCms } from "@/lib/cms";

const STEPS = ["Personal", "Professional", "Podcast", "Consent"];
const DESIGNATIONS = ["Principal", "Vice Principal", "Teacher", "Coordinator", "Academic Head", "School Director", "Other"];
const TOPICS = ["Classroom Innovation", "AI in Education", "NEP", "School Leadership", "Teacher Motivation", "Digital Learning", "Student Success", "Assessment", "STEM", "Other"];
const EXPERIENCE = ["Under 2 Years", "2–5 Years", "5–10 Years", "10+ Years"];
const LANGUAGES = ["English", "Hindi", "Both"];
const MODES = ["Online", "Offline", "Hybrid"];
const GENDERS = ["Female", "Male", "Prefer not to say", "Other"];

const EMPTY = {
  full_name: "", gender: "", age: "", mobile: "", whatsapp: "", email: "", district: "", state: "",
  school_name: "", designation: "", subject: "", experience: "", qualifications: "", awards: "", specialization: "",
  preferred_topic: "", podcast_title: "", description: "", key_takeaways: [], preferred_language: "English",
  preferred_mode: "Online", preferred_date: "", preferred_time: "", special_requirements: "", photo: "",
  participation_type: "", consent_podcast: false, consent_declaration: false,
  // Legacy fields kept for backward compat with existing submissions
  consent_media: false, consent_school_permission: false, permission_later: false, permission_letter_url: "", consent_legal: false, consent_terms: false, consent_privacy: false,
};

const inputCls = "w-full px-4 outline-none text-sm bg-white transition-all";
const inputStyle = { height: 56, borderRadius: 16, border: "1px solid #E2E8F0" };
const fh = {
  onFocus: (e) => { e.target.style.borderColor = "#3B68E7"; e.target.style.boxShadow = "0 0 0 2px #3B68E7"; },
  onBlur: (e) => { e.target.style.borderColor = "#E2E8F0"; e.target.style.boxShadow = "none"; },
};

const Field = ({ label, required, help, children }) => (
  <label className="block">
    <span className="text-xs font-semibold mb-1.5 block" style={{ color: "#64748B" }}>
      {label}{required && <span style={{ color: "#DC2626" }}> *</span>}
    </span>
    {children}
    {help && <span className="text-[11px] mt-1 block" style={{ color: "#94A3B8" }}>{help}</span>}
  </label>
);

const CardOption = ({ options, value, onChange }) => (
  <div className="grid grid-cols-3 gap-2">
    {options.map((o) => (
      <button
        key={o} type="button" onClick={() => onChange(o)}
        className="py-2.5 rounded-2xl text-sm font-medium transition-all"
        style={{
          border: `1.5px solid ${value === o ? "#3B68E7" : "#E2E8F0"}`,
          backgroundColor: value === o ? "#3B68E714" : "#FFFFFF",
          color: value === o ? "#3B68E7" : "#374151",
        }}
      >
        {o}
      </button>
    ))}
  </div>
);

export default function PodcastBookingModal({ open, onClose }) {
  const { site } = useCms();
  const brandName = site?.company_name || "";
  const [step, setStep] = useState(0);
  const [data, setData] = useState(EMPTY);
  const [takeawayInput, setTakeawayInput] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const [uploadingDoc, setUploadingDoc] = useState(false);
  const [uploadingLetter, setUploadingLetter] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const [docs, setDocs] = useState([]); // { name, url }
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState("");

  const set = (k, v) => setData((d) => ({ ...d, [k]: v }));

  const uploadFile = async (file) => {
    const fd = new FormData();
    fd.append("file", file);
    const r = await api.post("/podcast-bookings/upload", fd);
    return r.data.url;
  };

  const onPhotoFile = async (file) => {
    if (!file || !file.type.startsWith("image/")) { toast.error("Please choose an image file"); return; }
    setUploadingPhoto(true);
    try { set("photo", await uploadFile(file)); } catch (err) { toast.error(err?.response?.data?.detail || "Photo upload failed"); }
    finally { setUploadingPhoto(false); }
  };

  const onDocFiles = async (files) => {
    setUploadingDoc(true);
    try {
      for (const file of Array.from(files)) {
        const url = await uploadFile(file);
        setDocs((d) => [...d, { name: file.name, url }]);
      }
    } catch (err) { toast.error(err?.response?.data?.detail || "Upload failed"); }
    finally { setUploadingDoc(false); }
  };

  const onLetterFile = async (file) => {
    if (!file) return;
    setUploadingLetter(true);
    try { set("permission_letter_url", await uploadFile(file)); toast.success("Permission letter uploaded"); }
    catch (err) { toast.error(err?.response?.data?.detail || "Upload failed"); }
    finally { setUploadingLetter(false); }
  };

  const addTakeaway = (e) => {
    if ((e.key === "Enter" || e.key === ",") && takeawayInput.trim()) {
      e.preventDefault();
      if (!data.key_takeaways.includes(takeawayInput.trim())) {
        set("key_takeaways", [...data.key_takeaways, takeawayInput.trim()]);
      }
      setTakeawayInput("");
    }
  };

  const stepValid = (s) => {
    if (s === 0) return data.full_name && data.mobile && data.email;
    if (s === 1) return data.school_name && data.designation;
    if (s === 2) return data.preferred_topic;
    return true;
  };

  const next = () => {
    if (!stepValid(step)) { setError("Please fill in the required fields before continuing."); return; }
    setError("");
    setStep((s) => Math.min(s + 1, STEPS.length - 1));
  };
  const back = () => { setError(""); setStep((s) => Math.max(s - 1, 0)); };

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (!data.participation_type) { setError("Please select your participation type — choose one of the two options."); return; }
    if (!data.consent_podcast)     { setError("Please agree to the Podcast Participation Terms to continue."); return; }
    if (!data.consent_declaration) { setError("Please confirm the Declaration to continue."); return; }
    setSubmitting(true);
    try {
      await api.post("/podcast-bookings", {
        ...data,
        // consent_media, consent_terms, consent_privacy are covered by the
        // Podcast Participation Terms checkbox — derive from consent_podcast.
        consent_media: data.consent_podcast,
        consent_terms: data.consent_podcast,
        consent_privacy: data.consent_podcast,
        special_requirements: [data.special_requirements, docs.length ? `Supporting files: ${docs.map((d) => d.name).join(", ")}` : ""].filter(Boolean).join("\n"),
      });
      setSuccess(true);
    } catch (err) {
      setError(err?.response?.data?.detail || "Could not submit your application. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const close = () => {
    onClose();
    setTimeout(() => { setSuccess(false); setData(EMPTY); setDocs([]); setStep(0); setError(""); }, 300);
  };

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
          className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm flex items-start sm:items-center justify-center p-2 sm:p-4 overflow-y-auto"
          onClick={close} role="dialog" aria-modal="true" aria-label="Share your educational journey"
          onKeyDown={(e) => e.key === "Escape" && close()}
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.97, y: 12 }} animate={{ opacity: 1, scale: 1, y: 0 }} exit={{ opacity: 0, scale: 0.97, y: 12 }}
            transition={{ duration: 0.25 }}
            className="bg-white w-full my-4 sm:my-0"
            style={{ borderRadius: 28, maxWidth: 900 }}
            onClick={(e) => e.stopPropagation()}
            data-testid="podcast-booking-modal"
          >
            {success ? (
              <div className="p-8 sm:p-14 text-center">
                <motion.div initial={{ scale: 0 }} animate={{ scale: 1 }} transition={{ type: "spring", duration: 0.4 }} className="w-20 h-20 rounded-full grid place-items-center mx-auto mb-6" style={{ backgroundColor: "#ECFDF5" }}>
                  <CheckCircle2 className="w-10 h-10" style={{ color: "#16A34A" }} />
                </motion.div>
                <h2 className="rebrand-serif text-2xl sm:text-3xl font-bold" style={{ color: "#1A202C" }}>Application Submitted Successfully</h2>
                <p className="mt-3 max-w-md mx-auto" style={{ color: "#64748B" }}>
                  Thank you for applying. Our team will review your submission. You will receive updates via email
                  after review.
                </p>
                <div className="mt-8 flex flex-wrap gap-3 justify-center">
                  <Link to="/" onClick={close} className="text-sm font-medium" style={{ border: "1px solid #E2E8F0", color: "#1A202C", borderRadius: 50, padding: "11px 22px" }}>
                    Return Home
                  </Link>
                  <button onClick={close} className="text-sm font-semibold text-white transition-transform hover:scale-[1.02]" style={{ backgroundColor: "#3B68E7", borderRadius: 50, padding: "12px 24px" }}>
                    View Podcast Page
                  </button>
                </div>
              </div>
            ) : (
              <div style={{ backgroundColor: "#F8FAFC", borderRadius: 28 }} className="max-h-[95vh] overflow-y-auto">
                {/* Hero */}
                <div className="p-6 sm:p-10 pb-4 sm:pb-6 relative">
                  <button onClick={close} aria-label="Close" className="absolute top-4 right-4 w-8 h-8 grid place-items-center rounded-full hover:bg-black/5">
                    <X className="w-4 h-4" />
                  </button>
                  <div className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-semibold mb-4" style={{ backgroundColor: "#3B68E714", color: "#3B68E7" }}>
                    <Mic className="w-3 h-3" /> Podcast Guest Application
                  </div>
                  <h2 className="rebrand-serif text-2xl sm:text-3xl font-bold max-w-lg" style={{ color: "#1A202C" }}>Share Your Educational Journey</h2>
                  <p className="mt-2 text-sm max-w-lg" style={{ color: "#64748B" }}>
                    Inspire educators across the country by sharing your experiences, innovative teaching practices,
                    leadership journey, and classroom success stories.
                  </p>

                  {/* Progress tracker */}
                  <div className="mt-6 flex items-center gap-2">
                    {STEPS.map((label, i) => (
                      <div key={label} className="flex items-center gap-2 flex-1">
                        <div className="flex flex-col items-center gap-1 flex-1">
                          <div
                            className="w-7 h-7 rounded-full grid place-items-center text-xs font-semibold shrink-0 transition-colors"
                            style={{
                              backgroundColor: i < step ? "#16A34A" : i === step ? "#3B68E7" : "#E2E8F0",
                              color: i <= step ? "#FFFFFF" : "#94A3B8",
                            }}
                          >
                            {i < step ? <CheckCircle2 className="w-4 h-4" /> : i + 1}
                          </div>
                          <span className="text-[10px] font-medium hidden sm:block" style={{ color: i === step ? "#3B68E7" : "#94A3B8" }}>{label}</span>
                        </div>
                        {i < STEPS.length - 1 && <div className="h-0.5 flex-1 rounded-full" style={{ backgroundColor: i < step ? "#16A34A" : "#E2E8F0" }} />}
                      </div>
                    ))}
                  </div>
                </div>

                <form onSubmit={submit} className="px-6 sm:px-10 pb-8 sm:pb-10">
                  <AnimatePresence mode="wait">
                    <motion.div key={step} initial={{ opacity: 0, x: 12 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -12 }} transition={{ duration: 0.25 }}>

                      {step === 0 && (
                        <div className="bg-white p-6 sm:p-8" style={{ borderRadius: 24, boxShadow: "0 10px 25px rgba(0,0,0,0.05)" }}>
                          <SectionHeading icon={<User className="w-4 h-4" />} title="Personal Information" />
                          <div className="mt-5 grid sm:grid-cols-2 gap-4">
                            <Field label="Full name" required><input required value={data.full_name} onChange={(e) => set("full_name", e.target.value)} className={inputCls} style={inputStyle} {...fh} data-testid="pb-name" /></Field>
                            <Field label="Gender">
                              <select value={data.gender} onChange={(e) => set("gender", e.target.value)} className={inputCls} style={inputStyle} {...fh}>
                                <option value="">Select…</option>
                                {GENDERS.map((g) => <option key={g} value={g}>{g}</option>)}
                              </select>
                            </Field>
                            <Field label="Age"><input type="number" min="18" value={data.age} onChange={(e) => set("age", e.target.value)} className={inputCls} style={inputStyle} {...fh} /></Field>
                            <Field label="Mobile number" required><input required value={data.mobile} onChange={(e) => set("mobile", e.target.value)} className={inputCls} style={inputStyle} {...fh} data-testid="pb-mobile" /></Field>
                            <Field label="WhatsApp number" help="If different from mobile"><input value={data.whatsapp} onChange={(e) => set("whatsapp", e.target.value)} className={inputCls} style={inputStyle} {...fh} /></Field>
                            <Field label="Email address" required><input required type="email" value={data.email} onChange={(e) => set("email", e.target.value)} className={inputCls} style={inputStyle} {...fh} data-testid="pb-email" /></Field>
                            <Field label="City"><input value={data.district} onChange={(e) => set("district", e.target.value)} className={inputCls} style={inputStyle} {...fh} /></Field>
                            <Field label="State"><input value={data.state} onChange={(e) => set("state", e.target.value)} className={inputCls} style={inputStyle} {...fh} /></Field>
                          </div>
                        </div>
                      )}

                      {step === 1 && (
                        <div className="bg-white p-6 sm:p-8" style={{ borderRadius: 24, boxShadow: "0 10px 25px rgba(0,0,0,0.05)" }}>
                          <SectionHeading icon={<Briefcase className="w-4 h-4" />} title="Professional Information" />
                          <div className="mt-5 grid sm:grid-cols-2 gap-4">
                            <Field label="School name" required><input required value={data.school_name} onChange={(e) => set("school_name", e.target.value)} className={inputCls} style={inputStyle} {...fh} data-testid="pb-school" /></Field>
                            <Field label="Designation" required>
                              <select required value={data.designation} onChange={(e) => set("designation", e.target.value)} className={inputCls} style={inputStyle} {...fh}>
                                <option value="">Select…</option>
                                {DESIGNATIONS.map((d) => <option key={d} value={d}>{d}</option>)}
                              </select>
                            </Field>
                            <Field label="Subject"><input value={data.subject} onChange={(e) => set("subject", e.target.value)} className={inputCls} style={inputStyle} {...fh} /></Field>
                            <Field label="Experience">
                              <select value={data.experience} onChange={(e) => set("experience", e.target.value)} className={inputCls} style={inputStyle} {...fh}>
                                <option value="">Select…</option>
                                {EXPERIENCE.map((x) => <option key={x} value={x}>{x}</option>)}
                              </select>
                            </Field>
                            <Field label="Qualifications" help="e.g. M.Ed, B.Sc"><input value={data.qualifications} onChange={(e) => set("qualifications", e.target.value)} className={inputCls} style={inputStyle} {...fh} /></Field>
                            <Field label="Awards"><input value={data.awards} onChange={(e) => set("awards", e.target.value)} className={inputCls} style={inputStyle} {...fh} /></Field>
                            <Field label="Specialization" help="Your area of teaching focus"><input value={data.specialization} onChange={(e) => set("specialization", e.target.value)} className={inputCls} style={inputStyle} {...fh} /></Field>
                          </div>
                        </div>
                      )}

                      {step === 2 && (
                        <div className="bg-white p-6 sm:p-8" style={{ borderRadius: 24, boxShadow: "0 10px 25px rgba(0,0,0,0.05)" }}>
                          <SectionHeading icon={<Sparkles className="w-4 h-4" />} title="Podcast Information" />
                          <div className="mt-5 grid gap-4">
                            <Field label="Podcast topic" required>
                              <select required value={data.preferred_topic} onChange={(e) => set("preferred_topic", e.target.value)} className={inputCls} style={inputStyle} {...fh}>
                                <option value="">Select a topic…</option>
                                {TOPICS.map((t) => <option key={t} value={t}>{t}</option>)}
                              </select>
                            </Field>
                            <Field label="Tell us your story">
                              <textarea
                                value={data.description} onChange={(e) => set("description", e.target.value)}
                                style={{ minHeight: 180, borderRadius: 16, border: "1px solid #E2E8F0", padding: 20 }}
                                className="w-full outline-none text-sm resize-none" {...fh}
                              />
                              <div className="text-[11px] text-right mt-1" style={{ color: "#94A3B8" }}>{data.description.length} characters</div>
                            </Field>
                            <Field label="Key takeaways" help="Press Enter or comma to add">
                              <div className="flex flex-wrap gap-1.5 p-2.5" style={{ borderRadius: 16, border: "1px solid #E2E8F0" }}>
                                {data.key_takeaways.map((t, i) => (
                                  <span key={i} className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium" style={{ backgroundColor: "#3B68E714", color: "#3B68E7" }}>
                                    {t}
                                    <button type="button" onClick={() => set("key_takeaways", data.key_takeaways.filter((_, idx) => idx !== i))}><X className="w-3 h-3" /></button>
                                  </span>
                                ))}
                                <input
                                  value={takeawayInput} onChange={(e) => setTakeawayInput(e.target.value)} onKeyDown={addTakeaway}
                                  placeholder="e.g. AI, Leadership…" className="flex-1 min-w-[120px] outline-none text-sm px-1 py-1"
                                />
                              </div>
                            </Field>
                            <div className="grid sm:grid-cols-2 gap-4">
                              <Field label="Preferred language"><CardOption options={LANGUAGES} value={data.preferred_language} onChange={(v) => set("preferred_language", v)} /></Field>
                              <Field label="Preferred mode"><CardOption options={MODES} value={data.preferred_mode} onChange={(v) => set("preferred_mode", v)} /></Field>
                            </div>
                            <div className="grid sm:grid-cols-2 gap-4">
                              <Field label="Preferred date"><input type="date" value={data.preferred_date} onChange={(e) => set("preferred_date", e.target.value)} className={inputCls} style={inputStyle} {...fh} /></Field>
                              <Field label="Availability (time)"><input type="time" value={data.preferred_time} onChange={(e) => set("preferred_time", e.target.value)} className={inputCls} style={inputStyle} {...fh} /></Field>
                            </div>

                            <Field label="Profile photo">
                              <div
                                onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
                                onDragLeave={() => setDragOver(false)}
                                onDrop={(e) => { e.preventDefault(); setDragOver(false); onPhotoFile(e.dataTransfer.files?.[0]); }}
                                className="flex items-center gap-4 p-4 transition-colors"
                                style={{ borderRadius: 16, border: `2px dashed ${dragOver ? "#3B68E7" : "#E2E8F0"}`, backgroundColor: dragOver ? "#3B68E70A" : "transparent" }}
                              >
                                <div className="w-14 h-14 rounded-full overflow-hidden bg-gray-100 shrink-0 grid place-items-center">
                                  {data.photo ? <img src={data.photo} alt="" className="w-full h-full object-cover" /> : <ImageIcon className="w-5 h-5 text-gray-300" />}
                                </div>
                                <div className="flex-1 text-xs" style={{ color: "#64748B" }}>Drag & drop an image, or</div>
                                <label className="inline-flex items-center gap-1.5 text-xs font-medium cursor-pointer" style={{ border: "1px solid #E2E8F0", borderRadius: 50, padding: "8px 14px", color: "#1A202C" }}>
                                  {uploadingPhoto ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Upload className="w-3.5 h-3.5" />}
                                  {data.photo ? "Change" : "Browse"}
                                  <input type="file" accept="image/*" className="hidden" onChange={(e) => onPhotoFile(e.target.files?.[0])} />
                                </label>
                                {data.photo && (
                                  <button type="button" onClick={() => set("photo", "")} aria-label="Remove photo" className="w-8 h-8 grid place-items-center rounded-full hover:bg-rose-50 text-rose-500">
                                    <Trash2 className="w-4 h-4" />
                                  </button>
                                )}
                              </div>
                            </Field>

                            <Field label="Supporting files" help="Resume, presentation, or certificates">
                              <label className="flex items-center gap-2 p-4 cursor-pointer transition-colors" style={{ borderRadius: 16, border: "2px dashed #E2E8F0" }}>
                                {uploadingDoc ? <Loader2 className="w-4 h-4 animate-spin" style={{ color: "#3B68E7" }} /> : <FileText className="w-4 h-4" style={{ color: "#3B68E7" }} />}
                                <span className="text-xs" style={{ color: "#64748B" }}>Click to upload files</span>
                                <input type="file" multiple accept=".pdf,.doc,.docx,.ppt,.pptx,image/*" className="hidden" onChange={(e) => onDocFiles(e.target.files)} />
                              </label>
                              {docs.length > 0 && (
                                <div className="mt-2 flex flex-wrap gap-2">
                                  {docs.map((d, i) => (
                                    <span key={i} className="inline-flex items-center gap-1.5 text-xs px-2.5 py-1.5 rounded-lg bg-gray-50" style={{ border: "1px solid #E2E8F0" }}>
                                      <FileText className="w-3 h-3" /> {d.name}
                                      <button type="button" onClick={() => setDocs(docs.filter((_, idx) => idx !== i))}><X className="w-3 h-3" /></button>
                                    </span>
                                  ))}
                                </div>
                              )}
                            </Field>
                          </div>
                        </div>
                      )}

                      {step === 3 && (
                        <div className="grid gap-5">
                          <div className="bg-white p-6 sm:p-8" style={{ borderRadius: 24, boxShadow: "0 10px 25px rgba(0,0,0,0.05)" }}>
                            <div className="flex items-start gap-4">
                              <div className="w-12 h-12 rounded-full grid place-items-center shrink-0" style={{ backgroundColor: "#3B68E714" }}>
                                <ShieldCheck className="w-6 h-6" style={{ color: "#3B68E7" }} />
                              </div>
                              <div>
                                <h3 className="rebrand-serif text-lg font-bold" style={{ color: "#1A202C" }}>Consent &amp; Declaration</h3>
                                <p className="mt-1 text-sm" style={{ color: "#64748B" }}>Please complete all required fields below before submitting your application.</p>
                              </div>
                            </div>

                            {/* ── Participation Declaration ── */}
                            <div className="mt-6">
                              <p className="text-xs font-semibold uppercase tracking-wider mb-3" style={{ color: "#64748B" }}>
                                Participation Declaration <span style={{ color: "#DC2626" }}>*</span>
                              </p>
                              <p className="text-xs mb-3" style={{ color: "#94A3B8" }}>Select one option that applies to you.</p>

                              {/* Option 1 — School representative */}
                              <label
                                className="flex items-start gap-3 p-4 rounded-xl border cursor-pointer transition-all mb-3"
                                style={{
                                  borderColor: data.participation_type === "school_representative" ? "#4F46E5" : "#E2E8F0",
                                  backgroundColor: data.participation_type === "school_representative" ? "#EEF2FF" : "#FAFAFA",
                                }}
                                data-testid="pb-participation-school"
                              >
                                <input
                                  type="checkbox"
                                  checked={data.participation_type === "school_representative"}
                                  onChange={(e) => set("participation_type", e.target.checked ? "school_representative" : "")}
                                  className="mt-0.5 shrink-0 accent-[#4F46E5]"
                                  data-testid="pb-participation-school-checkbox"
                                />
                                <span className="text-sm leading-relaxed" style={{ color: "#374151" }}>
                                  I am participating as an authorized representative of my school. My school permits my participation.
                                </span>
                              </label>

                              {/* Option 2 — Personal capacity */}
                              <label
                                className="flex items-start gap-3 p-4 rounded-xl border cursor-pointer transition-all"
                                style={{
                                  borderColor: data.participation_type === "personal_capacity" ? "#4F46E5" : "#E2E8F0",
                                  backgroundColor: data.participation_type === "personal_capacity" ? "#EEF2FF" : "#FAFAFA",
                                }}
                                data-testid="pb-participation-personal"
                              >
                                <input
                                  type="checkbox"
                                  checked={data.participation_type === "personal_capacity"}
                                  onChange={(e) => set("participation_type", e.target.checked ? "personal_capacity" : "")}
                                  className="mt-0.5 shrink-0 accent-[#4F46E5]"
                                  data-testid="pb-participation-personal-checkbox"
                                />
                                <span className="text-sm leading-relaxed" style={{ color: "#374151" }}>
                                  I am participating in my personal capacity and my views expressed in the podcast are my own. They do not represent the views of my school.
                                </span>
                              </label>

                              {!data.participation_type && (
                                <p className="mt-2 text-xs" style={{ color: "#DC2626" }}>Please select one participation option above.</p>
                              )}
                            </div>

                            {/* ── Podcast Terms Consent ── */}
                            <div className="mt-5 space-y-3">
                              <p className="text-xs font-semibold uppercase tracking-wider" style={{ color: "#64748B" }}>
                                Terms Consent <span style={{ color: "#DC2626" }}>*</span>
                              </p>
                              <label
                                className="flex items-start gap-3 p-4 rounded-xl border cursor-pointer transition-all"
                                style={{
                                  borderColor: data.consent_podcast ? "#4F46E5" : "#E2E8F0",
                                  backgroundColor: data.consent_podcast ? "#EEF2FF" : "#FAFAFA",
                                }}
                              >
                                <input
                                  type="checkbox"
                                  required
                                  checked={data.consent_podcast}
                                  onChange={(e) => set("consent_podcast", e.target.checked)}
                                  data-testid="pb-consent-podcast"
                                  className="mt-0.5 shrink-0 accent-[#4F46E5]"
                                />
                                <span className="text-sm" style={{ color: "#374151" }}>
                                  I have read and agree to the{" "}
                                  <a href="/podcast-terms" target="_blank" rel="noreferrer"
                                    style={{ color: "#4F46E5", textDecoration: "underline", fontWeight: 500 }}
                                    onClick={(e) => e.stopPropagation()}
                                  >
                                    Podcast Participation Terms
                                  </a>.
                                </span>
                              </label>
                            </div>

                            {/* ── Declaration ── */}
                            <div className="mt-5 space-y-3">
                              <p className="text-xs font-semibold uppercase tracking-wider" style={{ color: "#64748B" }}>
                                Declaration <span style={{ color: "#DC2626" }}>*</span>
                              </p>
                              <label
                                className="flex items-start gap-3 p-4 rounded-xl border cursor-pointer transition-all"
                                style={{
                                  borderColor: data.consent_declaration ? "#059669" : "#E2E8F0",
                                  backgroundColor: data.consent_declaration ? "#F0FDF4" : "#FAFAFA",
                                }}
                              >
                                <input
                                  type="checkbox"
                                  required
                                  checked={data.consent_declaration}
                                  onChange={(e) => set("consent_declaration", e.target.checked)}
                                  data-testid="pb-consent-declaration"
                                  className="mt-0.5 shrink-0 accent-emerald-600"
                                />
                                <span className="text-sm leading-relaxed" style={{ color: "#374151" }}>
                                  <span style={{ fontWeight: 600 }}>Declaration: </span>
                                  I declare that all information provided in this application is accurate and complete. I understand that providing false information may result in disqualification.
                                </span>
                              </label>

                              <p className="text-xs" style={{ color: "#94A3B8", paddingLeft: 4 }}>
                                All required fields above must be completed before submission.
                              </p>
                            </div>
                          </div>

                          <div className="p-5 flex items-start gap-3" style={{ borderRadius: 16, backgroundColor: "#3B68E70D", border: "1px solid #3B68E71A" }}>
                            <Info className="w-4 h-4 mt-0.5 shrink-0" style={{ color: "#3B68E7" }} />
                            <p className="text-xs leading-relaxed" style={{ color: "#1A202C" }}>
                              Applications are reviewed by our editorial team. After approval you will receive: an
                              email confirmation, podcast schedule, recording instructions, consent confirmation, and
                              preparation guidelines.
                            </p>
                          </div>
                        </div>
                      )}
                    </motion.div>
                  </AnimatePresence>

                  {error && <div className="mt-4 rounded-lg bg-rose-50 border border-rose-200 text-rose-700 text-xs px-3 py-2.5">{error}</div>}

                  <div className="mt-6 flex items-center justify-between gap-3">
                    {step > 0 ? (
                      <button type="button" onClick={back} className="inline-flex items-center gap-1.5 text-sm font-medium" style={{ color: "#64748B" }}>
                        <ChevronLeft className="w-4 h-4" /> Back
                      </button>
                    ) : <button type="button" onClick={close} className="text-sm font-medium" style={{ color: "#64748B" }}>Maybe Later</button>}

                    {step < STEPS.length - 1 ? (
                      <button
                        type="button" onClick={next}
                        className="inline-flex items-center gap-2 text-white text-sm font-semibold transition-transform hover:scale-[1.02]"
                        style={{ backgroundColor: "#3B68E7", borderRadius: 50, padding: "12px 24px" }}
                      >
                        Continue <ChevronRight className="w-4 h-4" />
                      </button>
                    ) : (
                      <button
                        type="submit" disabled={submitting} data-testid="pb-submit"
                        className="text-white text-sm font-semibold transition-transform hover:scale-[1.02] disabled:opacity-60"
                        style={{ backgroundColor: "#3B68E7", borderRadius: 50, padding: "12px 26px", boxShadow: "0 4px 12px rgba(59,104,231,0.3)" }}
                      >
                        {submitting ? "Submitting…" : "Submit Application"}
                      </button>
                    )}
                  </div>
                </form>
              </div>
            )}
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

const SectionHeading = ({ icon, title }) => (
  <div className="flex items-center gap-2">
    <span className="w-8 h-8 rounded-lg grid place-items-center" style={{ backgroundColor: "#3B68E714", color: "#3B68E7" }}>{icon}</span>
    <h3 className="font-semibold" style={{ color: "#1A202C" }}>{title}</h3>
  </div>
);